import * as React from 'react'
import { cn } from '../../lib/cn'
import type { AudienceKind } from './AudienceCard'

/**
 * AudienceSwitcher — header 3-pill segmented control.
 *
 * Design plan reference,
 * section 22.2 (audience landing top nav) + section 23 (active pill
 * has a 2px brand-green underline that slides in from the left).
 *
 * Renders as buttons by default. When `hrefs` is provided per
 * audience, renders as `<a>` so the switcher is server-navigable
 * (SEO-friendly and works without JS) — the SPA pattern: pass
 * `onChange` to intercept the click for client-side navigation.
 *
 * Labels default to full names per the abbreviation rule
 * (feedback_abbreviation_context). Apps can override per-audience
 * via the `labels` prop when space is tight.
 *
 * Animation: the active underline is an absolutely-positioned
 * `<span>` per pill that uses `transform: scaleX()` with
 * `transform-origin: left`. Transition is 200ms ease-out; respects
 * `prefers-reduced-motion`.
 */

const DEFAULT_LABELS: Record<AudienceKind, string> = {
  developer: 'Developer',
  tenant_admin: 'Tenant admin',
  cp_admin: 'Control plane admin',
}

const AUDIENCES: AudienceKind[] = ['developer', 'tenant_admin', 'cp_admin']

export interface AudienceSwitcherProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onChange'> {
  /** Currently-active audience. Controlled by the caller. */
  active: AudienceKind
  /**
   * Fired when the user picks a different audience. When `hrefs` is
   * also set, the click event is intercepted (preventDefault) so the
   * caller's router can handle navigation.
   */
  onChange?: (audience: AudienceKind) => void
  /**
   * Per-audience URLs. When provided for an audience, that pill renders
   * as `<a href>` (server-navigable). When omitted, the pill renders as
   * `<button>` and the only way to switch is via onChange.
   */
  hrefs?: Partial<Record<AudienceKind, string>>
  /** Override the default audience labels. Useful in tight headers. */
  labels?: Partial<Record<AudienceKind, string>>
}

export const AudienceSwitcher = React.forwardRef<HTMLElement, AudienceSwitcherProps>(
  ({ active, onChange, hrefs, labels, className, ...rest }, ref) => {
    const resolvedLabels = { ...DEFAULT_LABELS, ...labels }

    return (
      <nav
        ref={ref}
        aria-label="Audience"
        className={cn('inline-flex items-center gap-1', className)}
        {...rest}
      >
        {AUDIENCES.map((audience) => {
          const isActive = audience === active
          const label = resolvedLabels[audience]
          const href = hrefs?.[audience]

          const inner = (
            <>
              <span className="whitespace-nowrap">{label}</span>
              {/* Animated underline. transform-origin: left; scale-x 0 -> 1
                  slides the bar in from the left over 200ms. Static
                  border-bottom would jump; scale-x animates smoothly
                  without affecting layout (the .h-[2px] reserves space). */}
              <span
                aria-hidden="true"
                className={cn(
                  'absolute bottom-0 left-2 right-2 h-[2px] rounded-pill',
                  'bg-[var(--color-ia-sidebar-active-border)]',
                  'origin-left transition-transform duration-200 ease-out motion-reduce:transition-none',
                  isActive ? 'scale-x-100' : 'scale-x-0',
                )}
              />
            </>
          )

          const baseClasses = cn(
            'relative px-3 py-2 text-body-sm transition-colors duration-100 motion-reduce:transition-none',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-md',
            isActive
              ? 'text-ia-text font-semibold'
              : 'text-ia-text-secondary hover:text-ia-text',
          )

          // <a href> path when an href is supplied for this audience.
          // SPA-friendly: when onChange is also set, click intercepts and
          // defers navigation to the handler.
          if (href) {
            return (
              <a
                key={audience}
                href={href}
                aria-current={isActive ? 'page' : undefined}
                onClick={(ev) => {
                  if (onChange) {
                    ev.preventDefault()
                    onChange(audience)
                  }
                }}
                className={baseClasses}
              >
                {inner}
              </a>
            )
          }

          // Button path — no href; the only way to switch is via onChange.
          return (
            <button
              key={audience}
              type="button"
              aria-current={isActive ? 'page' : undefined}
              onClick={() => onChange?.(audience)}
              className={baseClasses}
            >
              {inner}
            </button>
          )
        })}
      </nav>
    )
  },
)
AudienceSwitcher.displayName = 'AudienceSwitcher'
